import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Navbar from './features/Navbar';
import Login from './components/Authentication/Login';
import InstructorProfile from './components/Authentication/InstructorProfile';
import ResetPassword from './components/Authentication/ResetPassword';
import VerifyEmail from './components/Authentication/VerifyEmail';
import DashboardPage from './components/DashboardPage';
import IeltsDashboard from './components/IeltsDashboard';
import SkillDashboard from './components/SkillDashboard';
import MockExamSelector from './components/MockExamSelector';
import ExamInstructions from './components/ExamInstructions';
import ReadingEditor from './components/ReadingEditor';
import SpeakingEditor from './components/SpeakingEditor';
import WritingEditor from './components/WritingEditor';
import ListeningEditor from './components/ListeningEditor';
import PerformanceDashboard from './components/PerformanceDashboard';
import TeacherDashboard from './components/TeacherDashboard';
import AdminPortalPage from './components/AdminPortalPage';

// Import Performance Directory and Results Hub components (Mock 1 & Mock 2)
import PerformanceDirectory from './components/performance/PerformanceDirectory';
import Reading1Results from './components/performance/mock-exam/Reading1Results';
import Reading2Results from './components/performance/mock-exam/Reading2Results';

/**
 * ProtectedRoute Component: EV-A2-020 - Enforces session verification and role-based access control.
 * Ensures deterministic routing where students land on /dashboard and staff are redirected.
 */
const ProtectedRoute = ({ children, allowedRole }) => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  if (allowedRole && user.role !== allowedRole && user.role !== 'admin') {
    if (user.role === 'teacher') return <Navigate to="/teacher/analytics" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// TeacherRoute component: EV-A2-020 - Restricts access strictly to teachers and administrators.
const TeacherRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  return (user.role === 'teacher' || user.role === 'admin') ? children : <Navigate to="/dashboard" replace />;
};

/**
 * AdminRoute component: EV-A4-003 - Enforces explicit least-privilege access control for administrators.
 */
const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  return user.role === 'admin' ? children : <Navigate to="/dashboard" replace />;
};

export const AppRouter = () => {
  return (
    <Router>
      <Navbar />
      <Routes>
        {/* Public Authentication & Profile Routes */}
        <Route path="/" element={<Login />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/teacher-profile" element={<InstructorProfile />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* Main Student Dashboard */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute allowedRole="student">
              <DashboardPage />
            </ProtectedRoute>
          } 
        />

        {/* Teacher Analytics Route (Protected by TeacherRoute) */}
        <Route 
          path="/teacher/analytics" 
          element={
            <TeacherRoute>
              <TeacherDashboard />
            </TeacherRoute>
          } 
        />

        {/* Administration Portal Route (Protected by AdminRoute - EV-A4-003) */}
        <Route 
          path="/admin/portal" 
          element={
            <AdminRoute>
              <AdminPortalPage />
            </AdminRoute>
          } 
        />

        {/* IELTS Core Routes */}
        <Route path="/ielts" element={<ProtectedRoute allowedRole="student"><IeltsDashboard /></ProtectedRoute>} />
        <Route path="/ielts/:skill" element={<ProtectedRoute allowedRole="student"><SkillDashboard /></ProtectedRoute>} />
        
        {/* Unified Skill Navigation Routes */}
        <Route path="/ielts/:skill/lessons" element={<ProtectedRoute allowedRole="student"><SkillDashboard /></ProtectedRoute>} />
        <Route path="/ielts/:skill/practice" element={<ProtectedRoute allowedRole="student"><SkillDashboard /></ProtectedRoute>} /> 
        <Route path="/ielts/:skill/quizzes" element={<ProtectedRoute allowedRole="student"><SkillDashboard /></ProtectedRoute>} />
        <Route path="/ielts/:skill/assignments" element={<ProtectedRoute allowedRole="student"><SkillDashboard /></ProtectedRoute>} />
        <Route path="/ielts/:skill/vocabulary-bank" element={<ProtectedRoute allowedRole="student"><SkillDashboard /></ProtectedRoute>} />
        
        {/* Mapped Performance Directory & Results Hub Routes (Mock 1 & Mock 2 Support) */}
        <Route path="/ielts/:skill/performance" element={<ProtectedRoute allowedRole="student"><PerformanceDirectory /></ProtectedRoute>} />
        <Route path="/performance" element={<ProtectedRoute allowedRole="student"><PerformanceDirectory /></ProtectedRoute>} />
        <Route path="/performance/mock-exam/reading-1" element={<ProtectedRoute allowedRole="student"><Reading1Results /></ProtectedRoute>} />
        <Route path="/performance/mock-exam/reading-2" element={<ProtectedRoute allowedRole="student"><Reading2Results /></ProtectedRoute>} />

        {/* Mock Exam Workflow */}
        <Route path="/ielts/:skill/exams" element={<ProtectedRoute allowedRole="student"><MockExamSelector /></ProtectedRoute>} />
        <Route path="/ielts/:skill/instructions/:examId" element={<ProtectedRoute allowedRole="student"><ExamInstructions /></ProtectedRoute>} />
        
        {/* Skill-Specific Engines */}
        <Route path="/ielts/reading/exams/active/reading-1" element={<ProtectedRoute allowedRole="student"><ReadingEditor /></ProtectedRoute>} />
        <Route path="/ielts/reading/exams/active/reading-2" element={<ProtectedRoute allowedRole="student"><ReadingEditor /></ProtectedRoute>} />
        <Route path="/ielts/reading/exams/active/:examId" element={<ProtectedRoute allowedRole="student"><ReadingEditor /></ProtectedRoute>} />
        <Route path="/ielts/listening/exams/active/:examId" element={<ProtectedRoute allowedRole="student"><ListeningEditor /></ProtectedRoute>} />
        <Route path="/ielts/speaking/exams/active/:examId" element={<ProtectedRoute allowedRole="student"><SpeakingEditor /></ProtectedRoute>} />
        <Route path="/ielts/writing/editor/:examId" element={<ProtectedRoute allowedRole="student"><WritingEditor /></ProtectedRoute>} />
        
        {/* Performance Dashboard Routes */}
        <Route path="/ielts/results/:submissionId" element={<ProtectedRoute><PerformanceDashboard /></ProtectedRoute>} />
        <Route path="/dashboard/results/:submissionId" element={<ProtectedRoute><PerformanceDashboard /></ProtectedRoute>} />

        {/* Fallback Catch-All */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

export default AppRouter;